import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SparklesIcon, RefreshCwIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import type { Bi } from '../../data/sectors';
import { overviewMap as c, LOGO_BCITI, LOGO_ATNA } from '../../data/platformOverview';
import { ENTRY_ICONS, CORE_ICONS, DEST_ICONS, DEST_FRAGMENTS } from './mapParts';

/* Fixed design canvas — scaled to width by FitScale */
export const MAP_W = 1200;
export const MAP_H = 910;
const CX = 520;
const CY = 520;
const R_DISK = 112;
const R_NS = 178;
const R_SVC = 262;
const R_ECO = 345;

// Angles in degrees (0 = right, clockwise). The top wedge stays clear for the
// entry funnel and the right side for the flow toward the organization.
const CORE_ANGLES = [-126, -54, 30, 90, 150];
const SVC_ANGLES = [-130, -50, 35, 76, 117, 158, 199];
const ECO_ANGLES = [40, 66, 92, 118, 144, 170, 196, 222];
const ENTRY_X = [245, 355, 465, 575, 685, 795];
const ENTRY_LINE_Y = 124;
const DEST_X = 960;
const DEST_W = 230;
const DEST_Y = [CY - 140, CY, CY + 140];
const pol = (r: number, a: number): [number, number] => [CX + r * Math.cos(a * Math.PI / 180), CY + r * Math.sin(a * Math.PI / 180)];
const ENTRY_PATHS = ENTRY_X.map(x => {
  const endX = CX + (x - CX) * 0.05;
  return `M ${x} ${ENTRY_LINE_Y} C ${x} 210, ${CX + (x - CX) * 0.14} 215, ${endX} ${CY - R_NS}`;
});
const OUT_PATHS = DEST_Y.map((y, i) => {
  const sy = CY - 20 + i * 20;
  return `M ${CX + R_DISK} ${sy} C ${CX + 240} ${sy}, ${DEST_X - 120} ${y}, ${DEST_X} ${y}`;
});
const LOOP_PATH = `M ${DEST_X + DEST_W - 40} ${DEST_Y[0] - 50} C ${DEST_X + DEST_W - 40} 190, 1010 76, 866 76`;
function FlowDot({
  d,
  delay
}: {
  d: string;
  delay: number;
}) {
  return <motion.path d={d} fill="none" stroke="#5B50EE" strokeWidth={2} strokeLinecap="round" pathLength={1} strokeDasharray="0.02 1" initial={{
    strokeDashoffset: 1
  }} animate={{
    strokeDashoffset: 0
  }} transition={{
    duration: 3.6,
    delay,
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-5472" data-mp="24xwkyhcn7xy" />;
}
export function EcosystemMap() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  const reduce = useReducedMotion();
  return <div className="relative" style={{
    width: MAP_W,
    height: MAP_H
  }} data-id="element-5473" data-mp="1untoe9exhjfj">
      <svg width={MAP_W} height={MAP_H} className="absolute inset-0" aria-hidden="true" data-id="element-5474" data-mp="1zlva723qnwup">
        <defs data-id="element-5475" data-mp="11yd1tsdzowr8">
          <radialGradient id="po-halo" cx="50%" cy="50%" r="50%" data-id="element-5476" data-mp="11ymkeuwpg1mw">
            <stop offset="0%" stopColor="#EEEDFD" data-id="element-5477" data-mp="16wxth3f6e842" />
            <stop offset="100%" stopColor="#F7F6FE" stopOpacity="0" data-id="element-5478" data-mp="16xfwqk099mlh" />
          </radialGradient>
          <marker id="po-arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse" data-id="element-5479" data-mp="1bv93be5h0ima">
            <path d="M 0 1 L 7 5 L 0 9" fill="none" stroke="#8B85F0" strokeWidth="1.4" data-id="element-5480" data-mp="1gokbpjpxl7gb" />
          </marker>
        </defs>

        {/* Orbits */}
        <circle cx={CX} cy={CY} r={R_ECO + 40} fill="url(#po-halo)" data-id="element-5481" data-mp="o5n2yndd3xcp" />
        <circle cx={CX} cy={CY} r={R_ECO} fill="none" stroke="#E6E5F3" strokeWidth={1} strokeDasharray="2 7" data-id="element-5482" data-mp="31gpznfi7844d" />
        <circle cx={CX} cy={CY} r={R_SVC} fill="none" stroke="#DCDAF5" strokeWidth={1} strokeDasharray="3 6" data-id="element-5483" data-mp="2xel58ejxf3go" />
        <circle cx={CX} cy={CY} r={R_NS + 26} fill="#F4F3FE" data-id="element-5484" data-mp="2sf8kbzcm86gq" />
        <circle cx={CX} cy={CY} r={R_NS} fill="none" stroke="#D5D2F6" strokeWidth={1} data-id="element-5485" data-mp="2shur3wromhxw" />

        {/* Spokes — services to the core, ecosystem to the services ring */}
        {SVC_ANGLES.map(a => {
        const [x1, y1] = pol(R_SVC - 16, a);
        const [x2, y2] = pol(R_NS + 26, a);
        return <line key={`s${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#CFCBF5" strokeWidth={1} data-id="element-5486" data-mp="2dpaoqyccwehi" />;
      })}
        {ECO_ANGLES.map(a => {
        const [x1, y1] = pol(R_ECO - 16, a);
        const [x2, y2] = pol(R_SVC, a);
        return <line key={`e${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E0DEF3" strokeWidth={1} strokeDasharray="2 3" data-id="element-5487" data-mp="1zrm67x5rhjfm" />;
      })}
        {CORE_ANGLES.map(a => {
        const [x1, y1] = pol(R_DISK, a);
        const [x2, y2] = pol(R_NS - 14, a);
        return <line key={`c${a}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C4BFF4" strokeWidth={1} data-id="element-5488" data-mp="3uwvajazrel7z" />;
      })}

        {/* Entry funnel */}
        {ENTRY_PATHS.map(d => <path key={d} d={d} fill="none" stroke="#BEB9F3" strokeWidth={1.2} data-id="element-5489" data-mp="3l468t7u0glpu" />)}
        <line x1={CX} y1={CY - R_NS} x2={CX} y2={CY - R_DISK} stroke="#BEB9F3" strokeWidth={1.2} markerEnd="url(#po-arrow)" data-id="element-5490" data-mp="3q2hinikb2w82" />

        {/* Flow toward the organization */}
        {OUT_PATHS.map(d => <path key={d} d={d} fill="none" stroke="#BEB9F3" strokeWidth={1.2} markerEnd="url(#po-arrow)" data-id="element-5491" data-mp="3h4p3ceel3oko" />)}

        {/* Feedback loop */}
        <path d={LOOP_PATH} fill="none" stroke="#8B85F0" strokeWidth={1.2} strokeDasharray="4 5" markerEnd="url(#po-arrow)" data-id="element-5492" data-mp="374oholt8swdb" />

        {!reduce && <>
            {ENTRY_PATHS.map((d, i) => <FlowDot key={`fe${i}`} d={d} delay={i * 0.45} data-id="element-5493" data-mp="15e3bvhk2z4ca" data-mpi={i} />)}
            {OUT_PATHS.map((d, i) => <FlowDot key={`fo${i}`} d={d} delay={1.2 + i * 0.5} data-id="element-5494" data-mp="2pwnm712cx7l9" data-mpi={i} />)}
          </>}
      </svg>

      {/* Entry label + nodes */}
      <div className="absolute left-0 text-center" style={{
      top: 0,
      width: CX * 2
    }} data-id="element-5495" data-mp="2kuofpibbluk3">
        <p className="font-sans text-[11px] font-semibold tracking-[0.26em] uppercase text-[#121A3A]" data-id="element-5496" data-mp="2zni8g5fls76p">{pick(c.entryLabel)}</p>
        <p className="mt-1 font-sans text-[12.5px] text-[#7A8096]" data-id="element-5497" data-mp="38m3lxm67ww0m">{pick(c.entrySub)}</p>
      </div>
      {c.entries.map((e, i) => {
      const Icon = ENTRY_ICONS[e.key];
      return <div key={e.key} className="absolute flex flex-col items-center" style={{
        left: ENTRY_X[i],
        top: 56,
        transform: 'translateX(-50%)',
        width: 104
      }} data-id="element-5498" data-mp="3ik9n21k79y4f" data-mpi={i}>
            <span className="w-10 h-10 rounded-xl bg-white border border-[#E7E6F3] flex items-center justify-center shadow-[0_8px_18px_-14px_rgba(40,36,120,0.5)]" data-id="element-5499" data-mp="3dlq0q01p2kbj">
              <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.6} data-id="element-5500" data-mp="1j7p0pbuerr9v" />
            </span>
            <span className="mt-2 font-sans text-[12px] font-medium text-[#121A3A] whitespace-nowrap" data-id="element-5501" data-mp="3ja6qp08rjf88">{pick(e.label)}</span>
          </div>;
    })}

      {/* Central disk */}
      <div className="absolute rounded-full bg-white border border-[#E2E0FA] flex flex-col items-center justify-center shadow-[0_24px_60px_-30px_rgba(79,70,229,0.55)]" style={{
      left: CX - R_DISK,
      top: CY - R_DISK,
      width: R_DISK * 2,
      height: R_DISK * 2
    }} data-id="element-5502" data-mp="3sacyq6lozn00">
        <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-5503" data-mp="612ad481en8r" />
        <div className="mt-1 flex items-center" aria-label="BCITI | ATNA" data-id="element-5504" data-mp="b058e4plnt78">
          <img src={LOGO_BCITI} alt="BCITI" className="h-10 w-auto object-contain" data-id="element-5505" data-mp="ftzn0tqclhzg" />
          <span className="mx-1 text-[#121A3A]/25 text-lg font-light select-none" aria-hidden="true" data-id="element-5506" data-mp="jujkxziqwpmn">|</span>
          <img src={LOGO_ATNA} alt="ATNA" className="h-9 w-auto object-contain" data-id="element-5507" data-mp="oufo5wjmcd15" />
        </div>
        <p className="mt-1 max-w-[150px] text-center font-sans text-[9.5px] font-semibold tracking-[0.24em] uppercase leading-[1.5] text-[#4F46E5]" data-id="element-5508" data-mp="ttivjnn2mvca">{pick(c.coreLabel)}</p>
      </div>

      {/* Core — five trusted context nodes */}
      {c.core.map((n, i) => {
      const [x, y] = pol(R_NS, CORE_ANGLES[i]);
      const Icon = CORE_ICONS[n.key];
      return <div key={n.key} className="absolute flex items-center gap-1.5 rounded-full bg-white border border-[#D8D5F7] pl-1 pr-3 py-1 shadow-[0_6px_16px_-12px_rgba(79,70,229,0.6)]" style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-5509" data-mp="pwwmlwi68o1h" data-mpi={i}>
            <span className="w-6 h-6 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5510" data-mp="1hoci6g1phl3u">
              <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-5511" data-mp="1cvjbvnqla4pn" />
            </span>
            <span className="font-sans text-[12px] font-semibold text-[#121A3A] whitespace-nowrap" data-id="element-5512" data-mp="18uzgaolnz95m">{pick(n.label)}</span>
          </div>;
    })}

      {/* Services ring */}
      {c.services.map((s, i) => {
      const [x, y] = pol(R_SVC, SVC_ANGLES[i]);
      return <div key={s.fr} className="absolute flex items-center gap-1.5 rounded-full bg-white border border-[#E4E2F6] px-2.5 py-[5px]" style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-5513" data-mp="2k9nygoaamlig" data-mpi={i}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" data-id="element-5514" data-mp="2adkqv2wo9uo4" />
            <span className="font-sans text-[11.5px] font-medium text-[#2A3150] whitespace-nowrap" data-id="element-5515" data-mp="2fbco8cg368zp">{pick(s)}</span>
          </div>;
    })}
      <div className="absolute" style={{
      left: CX - R_SVC,
      top: CY,
      transform: 'translate(-50%, -50%)'
    }} data-id="element-5516" data-mp="331q8hgsfj3jq">
        <span className="rounded-full bg-[#F7F6FE] px-2 py-0.5 font-sans text-[9.5px] font-semibold tracking-[0.18em] uppercase text-[#4F46E5] whitespace-nowrap" data-id="element-5517" data-mp="37zibb5um4a2p">{pick(c.servicesLabel)}</span>
      </div>

      {/* Ecosystem ring */}
      {c.ecosystem.map((s, i) => {
      const [x, y] = pol(R_ECO, ECO_ANGLES[i]);
      return <div key={s.fr} className="absolute flex items-center gap-1.5 rounded-full bg-[#FBFBFD] border border-[#ECEDF2] px-2.5 py-[5px]" style={{
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-5518" data-mp="1mmp1sdjlpdkk" data-mpi={i}>
            <span className="w-1.5 h-1.5 rounded-full border border-[#9AA0B4]" data-id="element-5519" data-mp="3vw4uulu265p2" />
            <span className="font-sans text-[11px] text-[#5A6178] whitespace-nowrap" data-id="element-5520" data-mp="dq098h4b4ezj">{pick(s)}</span>
          </div>;
    })}
      <div className="absolute" style={{
      left: 24,
      top: 800,
      width: 240
    }} data-id="element-5521" data-mp="9o5bfmll4jqi">
        <p className="font-sans text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#121A3A]" data-id="element-5522" data-mp="3i3yqry46jp5z">{pick(c.ecosystemLabel)}</p>
        <p className="mt-2 font-serif text-[15px] leading-snug text-[#4A5268]" data-id="element-5523" data-mp="3d66ms5q9gclf">
          {c.ecosystemNote.map(l => <span key={l.fr} className="block" data-id="element-5524" data-mp="3qy3faavucoic">{pick(l)}</span>)}
        </p>
      </div>

      {/* Organization */}
      <p className="absolute font-sans text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#121A3A]" style={{
      left: DEST_X,
      top: DEST_Y[0] - 76
    }} data-id="element-5525" data-mp="3m3z2hazw9m9c">
        {pick(c.destinationsLabel)}
      </p>
      {c.destinations.map((d, i) => {
      const Icon = DEST_ICONS[d.key];
      const Frag = DEST_FRAGMENTS[d.key];
      return <div key={d.key} className="absolute grid grid-cols-[1fr_84px] items-center gap-3 rounded-2xl bg-white border border-[#ECEBF5] p-3.5 shadow-[0_18px_40px_-32px_rgba(40,36,120,0.5)]" style={{
        left: DEST_X,
        top: DEST_Y[i],
        width: DEST_W,
        transform: 'translateY(-50%)'
      }} data-id="element-5526" data-mp="17qn08uv0vptt" data-mpi={i}>
            <div data-id="element-5527" data-mp="tqc8lplw277l">
              <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5528" data-mp="tquc97avrnpj">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} data-id="element-5529" data-mp="oy16qh7l8bce" />
              </span>
              <p className="mt-2 font-sans text-[13px] font-semibold leading-tight text-[#121A3A]" data-id="element-5530" data-mp="k112mbuatinx">{pick(d.label)}</p>
            </div>
            <div className="rounded-lg bg-[#F7F7FB] p-1.5" aria-hidden="true" data-id="element-5531" data-mp="g08op5aheplb">
              <Frag data-id="element-5532" data-mp="axzyuhswo3nf" />
            </div>
          </div>;
    })}

      {/* Loop label */}
      <div className="absolute flex items-start gap-2" style={{
      left: 884,
      top: 116,
      width: 168
    }} data-id="element-5533" data-mp="61j77dmhz75l">
        <RefreshCwIcon className="mt-0.5 w-3.5 h-3.5 shrink-0 text-[#6D63F0]" strokeWidth={1.8} data-id="element-5534" data-mp="2hms79hr5ulwh" />
        <p className="font-sans text-[12px] italic leading-snug text-[#4F46E5]" data-id="element-5535" data-mp="2mld3l63aklfn">{pick(c.loop)}</p>
      </div>
    </div>;
}